import { AfterViewInit, ChangeDetectorRef, Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Params, Router, UrlSegment } from '@angular/router';
import { BehaviorSubject, Observable, catchError, map, of, throwError } from 'rxjs';

import { HisAuthService } from '../../auth/service/his-auth.service';
import { HisSessionStorageKeys } from '../../core/model/his-session-storage-keys';
import { HisUserProfileService } from '../../core/service/backend/his-userprofile.service';
import { HisNavigationService } from '../../core/service/base/his-navigation.service';
import { HisUiService } from '../../core/service/base/his-ui.service';
import { HisNotificationService } from '../../core/service/notifications/his-notification.service';
import { HisSearchConfiguration } from '../model/his-search-configuration';
import { HisWidgetBase } from '../model/his-widget-base';
import { HisWidgetBaseSelect } from '../model/his-widget-base-select';
import { HisWidgetGroup } from '../model/his-widget-group';
import { HisSearch, HisSearchCriteria, HisSearchCriteriaGroup, HisSearchGroup } from '../model/search/his-search';
import { HisSearchItem } from '../model/search/his-search-item';
import { HisSearchParser } from '../model/search/his-search-parser';
import { HisSearchParserHelper } from '../model/search/his-search-parser-helper';
import { HisSearchService } from '../service/his-search.service';
import { HisWidgetService } from '../service/his-widget.service';

//import * as _ from 'lodash';
import { HttpErrorResponse } from '@angular/common/http';
import { default as isNull, default as isUndefined } from 'lodash';
import { HisArrayUtils } from '../../core/utils';
import { HisOption } from '../model/his-option';
import { HisSearchCriteriaSet } from '../model/his-search-criteria-set';
import { HisSearchCriteriaSetRequest } from '../model/his-search-criteria-set-request';
import { HisSearchResultColumnSortHelper } from '../model/his-search-result-column-sort-helper';
import { HisSelectWidget } from '../model/his-widget-select';
import { OptionByIdRequest } from '../model/options/option-by-id-request';
import { SelectedOptions } from '../model/options/selected-options';
import { HisSearchConstants } from '../model/search/his-search-constants';
import { HisSearchNonCriteriaUrlParam } from '../model/search/his-search-non-criteria-url-param';
import { HisSearchCriteriaSetService } from '../service/his-search-criteria-set.service';
import { GenericSearchUtil } from '../utils/generic-search-util';
import { HisWidgetUtils } from '../utils/his-widget-utils';
import { HisSearchFormData } from './his-search-form-data';

@Directive()
export class HisSearchFormComponentBase extends HisSearchFormData implements OnInit, AfterViewInit, OnDestroy {
    private auth = inject(HisAuthService);
    private navigationService = inject(HisNavigationService);
    private notificationService = inject(HisNotificationService);
    private readonly route = inject( ActivatedRoute );
    private router = inject(Router);
    private userProfileService = inject(HisUserProfileService);
    private searchCriteriaSetService = inject(HisSearchCriteriaSetService);
    private cdRef = inject(ChangeDetectorRef);


    @Input() configFile: string;
    @Input() allResultConfigFiles: string[] = [];
    @Input() inputRequired = false;
    @Input() callerId: string;

    // true if the rendered page contains both the search form and the search result
    @Input() isSearchFormResult = false;
    // true if this is a search popup. In that case we don't want to register the url for the "back to search" button.
    @Input() isSearchPopup = false;

    @Input() isNestedSearchForm = false;

    @Input() showFormData = false;

    @Input() showSearchButton = true;

    @Input() showLinkToSearchHelp = true;

    // search constraints passed by parent component, like { 'Person.surname': 'Wolf', 'Person.firstname': 'Ulrich' }
    @Input() presetInputFields: Record<string, any> = {};

    @Input() searchShowSearchParamsBox = true;

    @Input() hiddenSearchCriteriaPaths: string[];

    @Input() renderTitle = true;

    // Specifies the heading level for the <his-heading> component.
    @Input() hierarchyLevel: number = 1;
    
    //userProfileKey to store current viewMode (liste/Tabelle). If the same key is used across pages, they share the same mode
    @Input() searchResultModeUserProfileKey: string = 'searchResultMode';

    @Output() searchSubmitted: EventEmitter<HisSearch> = new EventEmitter();

    @Output() formTitleLoaded: EventEmitter<string> = new EventEmitter();

    @ViewChild( 'formSearchButtons', { read: ElementRef } ) private formSearchButtonsComponentRef: ElementRef;

    title: string;

    // a search with quickSearchCriteria can be toggled to "extended search"
    hasQuickSearchCriteria: boolean;
    // is the search in "extended search" state?
    showAllSearchCriteria = false;

    // merged queryParams and presets
    allParams: Record<string, string> = {};

    defaultAllParams: Record<string, string> = {};

    showSearchForm = false;

    protected headingLevelWidgetTitle: number;

    protected searchMaskLeftGroupsAmount: number = 0;

    private readonly STANDARD = ' <Standard>';
    private readonly ERROR_STATUS = 422;
    private readonly ERROR_MESSAGE_TITLE = 'view.messages.error';
    private readonly EXCEPTION_DESCRIPTION = 'Validation exception.';
    protected contextPath: string;


    constructor() {
        const widgetService = inject(HisWidgetService);
        const searchService = inject(HisSearchService);
        const uiService = inject(HisUiService);

        super( widgetService, searchService, uiService );
    }

    ngOnInit() {
        this.contextPath = this.navigationService.getQisserverUrl();

        console.log( 'his-search-form.component#ngOnInit() #isSearchPopup =', this.isSearchPopup );
        this.headingLevelWidgetTitle = ( this.hierarchyLevel ?? 1 ) + 1;

        if ( this.isSearchFormResult ) {
            // In a search-form-result, we do not have search constraints as URL parameters
            console.log( 'his-search-form.component#ngOnInit(), #this.configFile =', this.configFile );
            this.allParams = this.presetInputFields ?? {};
            console.log( 'his-search-form.component#ngOnInit(), #isSearchFormResult, #presetInputFields =', this.presetInputFields );
            this.showSearchForm = true;
            this.init( this.configFile );
            return;
        }

        if ( !this.auth.hasAny( 'RIGHT_IS_LOGGED_IN' ) ) {
            this.searchShowSearchParamsBox = false;
        }
        console.log( 'his-search-form.component#ngOnInit() #user is logged in' );

        if ( this.isSearchPopup || this.isChangeSearch() || this.isNewSearch() || !this.searchShowSearchParamsBox
            || this.isNestedSearchForm ) {
            this.mergeParamsAndInitStandaloneSearchPage();
            return;
        }

        this.route.queryParams.subscribe( ( queryParams: Params ) => {
            console.log(
                'his-search-form.component#ngOnInit(), #this.configFile =', this.configFile,
                ', queryParams.configFile =', queryParams.configFile );
            // merge queryParams and presets
            this.allParams = {};
            for ( let key of Object.keys( this.presetInputFields ) ) {
                this.allParams[key] = this.presetInputFields[key];
            }
            for ( let key of Object.keys( queryParams ) ) {
                this.allParams[key] = queryParams[key];
            }
            console.log(
                'his-search-form.component#ngOnInit(), #queryParams =', queryParams,
                ', presetInputFields =', this.presetInputFields, '-> allParams =', this.allParams );
            // config file may be given per input parameter or per URL parameter.
            if ( !this.configFile ) {
                this.configFile = queryParams.configFile;
            }

            if ( !this.configFile ) {
                // da nützt auch der Aufruf this.initStandaloneSearchPage(); nichts,
                // das wäre dann ein Konfigurationsfehler
                return;
            }

            // Wenn es kein searchFormResult und auch kein changeSearch oder newSearch ist
            // dann prüfe, ob es ein Standard Suchset gibt.
            // Wenn ja, dann führe die Suche aus, ohne die Suchseite anzuzeigen.
            // Wenn nein, dann zeige die Suchseite an.

            // only used for search criteria sets. key for the session storage and 'flowName' in DB
            const componentId = this.callerId ?? this.searchCriteriaSetService.getFlowNameForSearchCriteriaSets();
            let searchCriteriaSetRequest = new HisSearchCriteriaSetRequest( this.configFile, null, componentId );
            this.searchCriteriaSetService.getSearchCriteriaSets( searchCriteriaSetRequest ).subscribe(
                ( searchSets: HisSearchCriteriaSet[] ) => {
                    console.log( 'his-search-form.component#ngOnInit() #searchSets', searchSets );
                    console.log( 'his-search-form.component#ngOnInit() #isEmptyArray', HisArrayUtils.isEmptyArray( searchSets ) );
                    if ( searchSets && !HisArrayUtils.isEmptyArray( searchSets ) ) {

                        let criteriaSet: HisSearchCriteriaSet = this.getStandardSet( searchSets );
                        console.log( 'his-search-form.component#ngOnInit() #criteriaSet', criteriaSet );
                        if ( criteriaSet ) {                      
                            criteriaSet.defaulttext = criteriaSet.defaulttext + this.STANDARD;
                            if (this.allResultConfigFiles?.length && criteriaSet.search) {
                                criteriaSet.search.allResultConfigFiles = this.allResultConfigFiles;
                            }

                            // 0: empty search result;
                            // 1: non - empty search result, grouping failed;
                            // 2: non - empty search result, grouping worked
                            this.searchService.validateInputs( criteriaSet.search ).pipe(
                                catchError( error => {
                                    console.error( 'his-search-form.component#ngOnInit() #validateInputs', error );
                                    this.initStandaloneSearchPage();
                                    throw error; //re-Throw the exception
                                } )
                            ).subscribe(
                                ( result: string[] | number ) => {
                                    if (typeof result === 'number') {
                                        console.log( 'resultCode', result );
                                        if ( result.toString() === '0' ) {
                                            this.initStandaloneSearchPage();
                                        }
                                    } else if (Array.isArray(result)  && result.length === 0) {
                                        this.initStandaloneSearchPage();
                                    }
                                }
                            );

                            this.searchService.loadSearchConfiguration( criteriaSet.configFile ).subscribe(
                                ( searchConfig: HisSearchConfiguration ) => {
                                    if ( searchConfig ) {
                                        console.log( 'onInit #searchConfig', searchConfig );
                                        this.title = searchConfig.pageTitle;
                                        this.formTitleLoaded.emit( this.title );

                                        this.searchConfiguration = searchConfig;

                                        this.storeSearchSetAtSessionStorage( criteriaSet );
                                        this.storeCompinentIdAtSessionStorage( componentId );

                                        this.registerCurrentUrlForBackToSearchButton();
                                        this.submitSearch( criteriaSet.search );
                                    }
                                } );
                        } else {
                            console.log( 'his-search-form.component#ngOnInit() #no standard search set defined' );
                            this.initStandaloneSearchPage();
                        }
                    } else {
                        console.log( 'his-search-form.component#ngOnInit() #no search set defined' );
                        this.initStandaloneSearchPage();
                    }
                }
            );
        } );
    }
    
    ngOnDestroy(): void {
        // Entferne Suchkonfigurationsdatei für Abonnenten.
        // remove search configuration file for subscribers
        this.searchService.subjectSearchConfigurationValueChanged.next(null);
    }

    private mergeParamsAndInitStandaloneSearchPage() {
        this.showSearchForm = true;
        this.route.url.subscribe( ( url: UrlSegment[] ) => {
            console.log( 'HisSearchFormComponent.ngOnInit(): Loading route \'' + url.join( '/' ) + '\' ...' );
        } );

        this.route.queryParams.subscribe( ( queryParams: Params ) => {
            console.log(
                'HisSearchFormComponent.ngOnInit(): this.configFile =', this.configFile,
                ', queryParams.configFile =', queryParams.configFile );
            // merge queryParams and presets
            this.allParams = {};
            for ( let key of Object.keys( this.presetInputFields ) ) {
                this.allParams[key] = this.presetInputFields[key];
            }
            for ( let key of Object.keys( queryParams ) ) {
                this.allParams[key] = queryParams[key];
            }
            console.log(
                'HisSearchFormComponent.ngOnInit(): no searchFormResult, queryParams =', queryParams,
                ', presetInputFields =', this.presetInputFields, '-> allParams =', this.allParams );
            // config file may be given per input parameter or per URL parameter.
            this.init( this.configFile ? this.configFile : queryParams.configFile );
        } );

        this.registerCurrentUrlForBackToSearchButton();
    }

    private initStandaloneSearchPage() {
        this.showSearchForm = true;
        this.init( this.configFile );
        this.registerCurrentUrlForBackToSearchButton();
    }

    /**
     * @param configFile the name of the search configuration file
     */
    private init( configFile: string ) {
        console.log( 'HisSearchFormComponent.init(): configFile =', configFile, ', allParams =', this.allParams );
        this.configFile = configFile;

        this.searchService.loadSearchConfiguration( this.configFile ).subscribe(
            ( searchConfig: HisSearchConfiguration ) => {
                if ( !searchConfig ) {
                    // this was just the initialization of the observable, not the real data
                    return;
                }

                this.title = searchConfig.pageTitle;
                this.formTitleLoaded.emit( this.title );

                this.searchConfiguration = searchConfig;

                // let searchConstraints = queryParams ? queryParams : this.presetInputFields;
                this.hasQuickSearchCriteria = !!this.searchConfiguration.searchCriteria.quickSearchCriteria; // !! converts null to false
                // all search criteria are shown if there are no quickSearchCriteria or if the search is in "extended search" state
                let isExtendedSearch = this.allParams && this.allParams.extended ? true : false;
                this.showAllSearchCriteria = isExtendedSearch || !this.hasQuickSearchCriteria;

                // load initial select options
                this.widgetService.loadWidgetOptions( this.searchConfiguration, this.showAllSearchCriteria );

                // initialize search criteria
                this.widgetGroups = this.widgetService.getWidgetGroups( searchConfig );
                this.widgetGroups.forEach( g => {
                    g.widgets = g.widgets.filter( w =>
                        !( this.hiddenSearchCriteriaPaths?.length &&
                            this.hiddenSearchCriteriaPaths.some( hiddenGL => hiddenGL === w.id ) ) );
                } );

                for ( let widgetGroup of this.widgetGroups ) {
                    for ( let widget of widgetGroup.widgets ) {
                        this.resetOrApplyOperatorState( widget );
                        this.addPresetValueToAllParams( widget );
                    }
                }

                if ( this.isChangeSearch() ) {
                    let allParamsWithOutDefaultValue = { ...this.allParams };

                    for ( const widgetGroup of this.widgetGroups ) {
                        for ( const widget of widgetGroup.widgets ) {
                            delete allParamsWithOutDefaultValue[widget.id];
                        }
                    }
                    console.log( 'HisSearchFormComponent.init(): this.form=', this.form );
                    this.initializeFormWithPresetValues( allParamsWithOutDefaultValue );
                } else {

                    this.initializeFormWithPresetValues( this.allParams );
                }

                console.log( 'HisSearchFormComponent.init(): this.form=', this.form );

                // listen to value changes
                this.form.valueChanges.subscribe( model => {
                    console.log( 'HisSearchFormComponent.init(): Model changed to', model );
                    // console.log( 'form.value=', this.form.value ); // same as model

                    // pass value changes from model (this.form.value) to widgets
                    for ( let widgetGroup of this.widgetGroups ) {
                        for ( let widget of widgetGroup.widgets ) {
                            this.updateModelValue( model, widget );
                        }
                    }
                } );
            },
            ( error ) => {
                console.log( 'init() caught error:', error );
            }
        );
    }

    private resetOrApplyOperatorState( widget: HisWidgetBase<any> ) {
        if ( widget instanceof HisWidgetBaseSelect && !widget.not ) {
            // Explizites Zurücksetzen der Operatoren,
            // die bei der Breadcrumb-Navigation nicht zurückgesetzt werden
            // see hiszilla 246714
            if ( this.isBreadcrumbNavigation() ) {
                this.triggerOperator( false, widget );
                console.log( 'Assigned not', widget.not, 'to form control for widget', widget.id );
            } else {
                // Hiszilla 278471 - Sprachumschaltung in der generischen Suche führt zu unterschiedliches Verhalten (Zweck/Status)
                this.triggerOperator( this.getOperatorStateFromParamOrFalse( widget.id ), widget );
            }
        }
    }

    private addPresetValueToAllParams( widget: HisWidgetBase<any> ) {
        //If there is a default value in the search configuration file, then that value is set.
        //see hiszilla 340591
        if ( widget.value ) {
            if ( !this.allParams[widget.id] ) {
                this.allParams[widget.id] = widget.value;
            }
        }
    }

    ngAfterViewInit() {
        if ( this.widgetGroups ) {
            if ( this.allParams ) {
                this.restoreCriteriaGroupWidgets( this.allParams );
            }
            this.setOperatorValues( this.widgetGroups );
        }
        this.cdRef.detectChanges();
    }

    private isBreadcrumbNavigation(): boolean {
        if (!this.allParams) {
            return false;
        }
        for ( let key of Object.keys( this.allParams ) ) {
            // key = groups1.criteria0.field
            if (key.endsWith('field')) {
                return false;
            }
        }
        return true;
    }

    private getOperatorStateFromParamOrFalse(widgetId: string): boolean {
        if (!this.allParams) {
            return false;
        }
        for ( let key of Object.keys( this.allParams ) ) {
            // key = groups1.criteria0.field
            if (key.endsWith('field') && this.allParams[key] === widgetId) {
                const operatorKey = key.replace(HisSearchConstants.FIELD, HisSearchConstants.NOT);
                const operatorValue = this.allParams[operatorKey];

                if (operatorValue === 'true' || operatorValue === 'false') {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Restores criteria group widgets based on the provided search constraints.
     * Used when modifying an existing search ('Suche ändern').
     *
     * @param searchConstraints - The search constraints to restore widgets from
     */
    restoreCriteriaGroupWidgets( searchConstraints: any ) {
        // Convert flat constraints to HIS search items
        const searchItems = HisSearchParserHelper.convertFlatObjectFormatParamsToHisSearchItems( searchConstraints );
        const parsedSearch = HisSearchParser.decode( searchItems );

        // First populate not lazy widgets values and create all criteria groups
        this.populateNotLazyWidgetValues( parsedSearch );

        // Then populate lazy widgets values
        this.populateLazyWidgetValues( parsedSearch );
    }

    /**
     * Creates criteria groups from the parsed search data
     */
    private populateNotLazyWidgetValues( parsedSearch: HisSearch ) {
        for ( const searchGroup of parsedSearch.groups ) {
            this.copyWidgetsFromGroupAndCreateCriteriaGroup( searchGroup, false );
        }
    }

    /**
	 * Populates widget values from the parsed search criteria
	 *
	 * @remarks
	 * Not part of the public API - do not use directly in application code.
	 * This exists solely to handle the lazy-loading workflow in generic search.
	 *
	 * This is intentionally public ONLY to allow coordination between:
	 * - {@link HisSearchFormComponentBase}
	 * - {@link HisSearchSaveParamsComponent}
	 */
    public populateLazyWidgetValues( parsedSearch: HisSearch ): void {
        // Iterate through each search group in the parsed search
        for ( const searchGroup of parsedSearch.groups ) {
            // Process widgets that require lazy loading
            this.processCriteriaGroupWidgets( searchGroup );
        }
    }

    /**
     * Processes widgets for a specific criteria group, focusing on lazy-loaded widgets.
     * Maps search criteria to corresponding widgets and triggers async loading when needed.
     *
     * @param searchGroup - The search group containing criteria groups to process
     */
    private processCriteriaGroupWidgets( searchGroup: HisSearchGroup ): void {
        // Find the matching widget group based on the search group label
        const matchingWidgetGroup = HisWidgetUtils.getWidgetGroupWithLabel( searchGroup.groupLabel, this.widgetGroups );
        const widgetCriteriaGroups = matchingWidgetGroup?.criteriaGroups;
        const searchCriteriaGroups = searchGroup.criteriaGroups;

        // Process each criteria group within the widget group
        for ( const index in widgetCriteriaGroups ) {
            const currentWidgetCriteriaGroup = widgetCriteriaGroups[index];

            // Check each widget in the current criteria group
            for ( const widget of currentWidgetCriteriaGroup.widgets ) {
                // Process only lazy-loaded select widgets
                if ( this.isLazySelectWidget( widget ) ) {
                    const correspondingCriteriaGroup = searchCriteriaGroups[index];
                    this.handleLazySelectWidget( widget, correspondingCriteriaGroup );
                }
            }
        }
    }

    private isLazySelectWidget( widget: any ): widget is HisSelectWidget {
        return widget instanceof HisSelectWidget &&
            GenericSearchUtil.isLazySelectFrom( widget.optionsKey, widget.showDropdownIcon );
    }

    /**
     * Handles value restoration for lazy-loaded select widgets.
     * Finds matching criteria, loads the necessary options, and updates the widget.
     */
    private handleLazySelectWidget( widget: HisSelectWidget, criteriaGroup: HisSearchCriteriaGroup ): void {
        // Extract path suffix
        const widgetFullPath = widget.id;
        const widgetPathSuffix = widgetFullPath.split( ']' ).pop();

        // Find the criterion matching this widget's path
        const matchingCriterion = HisSearchParserHelper.getCriterionWithPath(
            widgetPathSuffix,
            criteriaGroup.criteria
        );

        // Exit if no matching criterion or no value
        if ( !matchingCriterion?.value ) return;

        // Load the options for this lazy widget and set its value
        this.loadAndSetLazyWidgetValue(
            widget.optionsKey,
            matchingCriterion.value,
            matchingCriterion.not
        ).subscribe( loadedOptions => {
            if ( loadedOptions && !HisArrayUtils.isEmptyArray( loadedOptions ) ) {
                // Update the widget with the only available option
                this.updateWidgetValues( widget, loadedOptions[0], matchingCriterion.not );
                this.addOrUpdateControl( widget, loadedOptions );
            }
        } );
    }

    /**
     * Updates widget values after loading
     */
    private updateWidgetValues( widget: HisSelectWidget, value: any, notValue: boolean ) {
        widget.value = value;
        widget.originalValue = value;
        widget.not = notValue;
        widget.originalNot = notValue;
    }

    /**
     * Loads values for a lazy-loaded widget
     */
    private loadAndSetLazyWidgetValue( optionsKey: string, id: string, notValue: boolean ): Observable<HisOption[]> {
        if ( !id ) return of( [] );

        const optionRequest = new OptionByIdRequest( optionsKey, [id] );

        return this.searchService.loadOptionsByIds( this.configFile, [optionRequest] ).pipe(
            map( ( results: SelectedOptions[] ) => {
                return results?.[0]?.selectedOptions ?? [];
            } )
        );
    }

    updateModelValue( model: any, widget: HisWidgetBase<any> ) {
        let newValue = model[widget.id];
        // console.log('widget.value=', widget.value, ', newValue=', newValue);
        if ( newValue !== widget.value ) {
            let debug = 'HisSearchFormComponent.listenToValueChanges(): Change widget';
            console.log( debug, widget.id, 'value from', widget.value, 'to', newValue );
            widget.value = newValue;
            // TODO different treatment for setting to null?
        }
    }

    private getOperatorValue( params: Record<string, any>, widget: HisWidgetBaseSelect<any>, groupLabel: string ): boolean {
        // old url format
        // #key Person.gender.id #value 2
        // #key Person.gender.id(NOT) #value true
        // key = widget.id + '(NOT)'
        if ( params[widget.id + '(NOT)'] ) {
            console.log( 'his-search-form#getOperatorValue() #set for key with old url format', widget );
            return true;
        }

        // flat object url format:
        // #key groups0.criteria0.field #value Person.gender.id
        // #key groups0.criteria0.value #value 2
        // #key groups0.criteria0.not #value true
        console.log( 'his-search-form#getOperatorValue() #widget', widget );
        let flatObjOperatorValue: boolean = undefined;
        for ( let key of Object.keys( params ) ) {
            console.log( 'his-search-form#getOperatorValue() #key', key );
            if ( HisWidgetUtils.isMatchingKeyForWidget( key, params[key], widget.id, groupLabel ) ) {
                let notKey = key.replace( HisSearchConstants.FIELD, HisSearchConstants.NOT );
                console.log( 'his-search-form#getOperatorValue() #notKey', notKey );
                console.log( 'his-search-form#getOperatorValue() #flatObjOperatorValue', widget.id, flatObjOperatorValue );
                return params[notKey];
            }
        }
        return undefined;
    }

    private setOperatorValues( widgetGroups: HisWidgetGroup[] ) {
        // siehe Hiszilla 246714
        for ( let widgetGroup of widgetGroups ) {
            for ( let widget of widgetGroup.widgets ) {
                this.setOperators( widget as HisWidgetBaseSelect<any>, widgetGroup.label );
            }
            for ( let criteriaGroup of widgetGroup.criteriaGroups ) {
                for ( let widget of criteriaGroup.widgets ) {
                    this.setOperators( widget as HisWidgetBaseSelect<any>, widgetGroup.label );
                }
            }
        }
    }

    private setOperators( widget: HisWidgetBaseSelect<any>, groupLabel: string ) {
        let isSelectWidget = widget instanceof HisWidgetBaseSelect;
        if ( !isSelectWidget ) {
            console.log( 'his-search-form#init() #not a select widget', widget );
            return;
        }
        console.log( 'his-search-form#init() #select widget', widget );
        if (!this.allParams) {
            return;
        }
        let operatorValue = this.getOperatorValue( this.allParams, widget as HisWidgetBaseSelect<any>, groupLabel );
        console.log( 'his-search-form#init() #flatObjOperatorValue after for loop', widget.id, operatorValue );
        this.triggerOperator( operatorValue, widget );
    }

    /**
     * Switch from simple search to extended search or back.
     */
    onToggleQuickSearch() {
        this.showAllSearchCriteria = !this.showAllSearchCriteria;
        // load select options not present yet (typically after switch to "extended search")
        this.widgetService.loadWidgetOptions( this.searchConfiguration, this.showAllSearchCriteria );
    }

    /**
     * Submit a search where the search constraints are taken from the search mask.
     */
    submitSearchForm() {
        console.log( 'his-search-form.component#submitSearchForm() #this.widgetGroups', this.widgetGroups );
        // XXX Wegen Hiszilla 257303 deaktiviert. Stellt sich die Frage, ob das überhaupt benötigt wird
        // Damit wird das Beziehen der Daten aus this.getFormData() immer weiter abgelöst.
        // Für was war eigentlich diese doppelte Datenhaltung in den widgets und in form?
        let widgetGroups = this.validateWidgetGroupsValues( this.widgetGroups, this.getFormData() );

        let search = HisSearchParserHelper.createNewHisSearchByWidgetValues( widgetGroups, this.configFile );
        if (this.allResultConfigFiles?.length && search) {
            search.allResultConfigFiles = this.allResultConfigFiles;
        }
        console.log( 'his-search-form.component#submitSearchForm() #search', search );
        this.submitSearch( search );
    }

    getNumberOfVisibleWidgetGroups(): number {
        if ( this.showAllSearchCriteria ) {
            return this.widgetGroups.length;
        }
        // Einfache Suche:
        let num = 0;
        for ( let widgetGroup of this.widgetGroups ) {
            if ( widgetGroup.quickSearch ) {
                num++;
            }
        }
        return num;
    }

    private validateWidgetGroupsValues( widgetGroups: HisWidgetGroup[], params: any ): HisWidgetGroup[] {
        for ( let widgetGroup of widgetGroups ) {

            this.checkWidgetValueAndReplaceIfNecessary( widgetGroup.widgets, params );

            for ( let criteriaGroup of widgetGroup.criteriaGroups ) {
                this.checkWidgetValueAndReplaceIfNecessary( criteriaGroup.widgets, params );
            }
        }
        return widgetGroups;
    }

    private checkWidgetValueAndReplaceIfNecessary( widgets: HisWidgetBase<any>[], params: any ) {
        for ( let widget of widgets ) {
            let paramValue = params[widget.id];
            if ( this.isWidgetValueEqualToParamValue( widget, paramValue ) ) {
                continue;
            }
            console.log(
                'his-search-form.component#validateWidgetGroupsValues() #widget value not equals paramValue',
                widget.id, paramValue );
            widget.value = paramValue;
        }
    }

    private isWidgetValueEqualToParamValue( widget: HisWidgetBase<any>, paramValue: any ) {
        return widget.value === paramValue
            || isNull( paramValue ) || isUndefined( paramValue ) ;
    }

    createHisSearchFromSearchData( configFile: string ): Observable<HisSearch> {
        if ( !configFile ) {
            throw new Error( 'HisSearchForm: No searchConfigFile present!' );
        }

        let obs = new BehaviorSubject( null );

        this.searchService.loadSearchConfiguration( configFile ).subscribe( ( searchConfiguration: HisSearchConfiguration ) => {
            //console.log( 'searchConfiguration', searchConfiguration );
            if ( !searchConfiguration ) {
                return;
            }

            let search: HisSearch = new HisSearch();
            search.configFile = configFile;
            let keys = Object.keys( this.allParams );
            if ( keys.length > 0 ) {
                for ( let key of keys ) {
                    // let value = this.getValueForKey(key, inputFields);
                    // let value = HisSearchParserHelper.convertValueToString(key, this.allParams[key]);
                    if ( !this.allParams[key] ) {
                        continue;
                    }

                    for ( let group of searchConfiguration.searchCriteria.group ) {
                        // if (group.and) {
                        // warum ist das nicht da?
                        // weil es nicht gesetet ist weil es nicht true ist?
                        // }

                        for ( let criterion of group.criteria ) {
                            // Gruppe setzen nur, wenn es mindestens ein Feld in der Gruppe gibt
                            if ( criterion.path === key ) {
                                console.log( 'his-search-form.component#createHisSearchFromSearchData() #criterion', criterion );
                                let criteria = new HisSearchCriteria();
                                criteria.field = key;
                                let paramValue = this.allParams[key];
                                // hier eine EXTRA Behandlung für Checkboxen, man ist das scheiße gelöst!!!!
                                // Checkbox Wert aus Suchkofiguration>value nur dann, wenn gecheckt, sonst kein value/option

                                criteria.value = HisSearchParserHelper.convertValueToString( key, paramValue, criterion.value );
                                if ( criterion.multipleSelect ) {
                                    if ( criterion.not ) {
                                        criteria.not = true;
                                    }
                                    // not implemented
                                    // if( widget.operator) {
                                    //     criteria.operator = widget.operator;
                                    // }
                                }
                                // wenn es die Gruppe gibt hinzufügen, wenn nicht, neue Gruppe
                                let searchGroup = search.groups.find( grp => grp.groupLabel === group.label );
                                if ( searchGroup ) {
                                    searchGroup.criteria.push( criteria );
                                    console.log( 'searchGroup.criteria.push(criteria)', searchGroup, criteria );
                                } else {
                                    let newGroup = new HisSearchGroup();
                                    newGroup.groupLabel = group.label;
                                    newGroup.criteria.push( criteria );
                                    search.groups.push( newGroup );
                                    console.log( 'search.groups.push(newGroup)', newGroup, criteria );
                                }

                                // Kriteriangruppen fehlen noch
                            }
                        }
                    }
                }
            }

            obs.next( search );
        } );

        return obs;
    }

    /**
     * Submit a search with the given search constraints.
     * May be used for headless searches without an input form.
     *
     * @param searchConstraints the search constraints to apply in the search
     */
    public submitSearch( search: HisSearch ) {
        console.log(
            'submitSearch(): Submitting search \'' + search.configFile + '\'',
            'with search constraints', search
        );
        console.log( 'his-search-form#submitSearch() #search', search );

        // delets all session storage keys with column sort
        HisSearchResultColumnSortHelper.removeAllSessionStorageKeysContainingSortOrder();

        if ( this.isSearchFormResult ) {
            // This page includes search form and search result -> just fire an event
            console.log( 'HisSearchFormComponent.submitSearch(): Firing submit-search event ...' );
            this.searchSubmitted.emit( search );
        } else if (this.inputRequired && !search.groups?.length) { 
            this.notificationService.warnI18nRes('genericSearch.missingSearchParameters');
        } else {
            // The search result will be displayed on a different page.
            // To show validation errors on the search mask we need a validation REST call
            // before the redirect to the search result URL:
            console.log( 'his-search-form: this.searchService.validateInputs #search', search );
            this.searchService.validateInputs( search ).subscribe(
                ( result: string[] | number ) => {
                    console.log( 'resultCode =', result );
                    // resultCode 0: empty search result;
                    // resultCode 1:non-empty search result, grouping failed;
                    // resultCode 2: non-empty search result, grouping worked

                    let route = '../searchresult';

                    // no validation error
                    if ((typeof result === 'number' &&  result.toString() === '0') || (Array.isArray(result)  && result.length === 0)) {
                        // remove old messages #248767
                        this.notificationService.clear();

                        // empty result
                        this.notificationService.info(
                            this.uiService.translate( 'genericSearch.emptyResult' ),
                            this.uiService.translate( 'view.messages.information' )
                        );

                        this.showSearchForm = true;
                        route = '../search';
                    }

                    // no validation error, non-empty result-> we want to retrieve the search result.
                    // Add search constraints to URL parameters and navigate to the search result page:
                    let queryParams: Params = {};

                    // addNavigationPosition
                    let urlParams = this.route.snapshot.queryParams;
                    // console.log('his-search-form#submitSearch() #addNavigationPosition #urlParams', urlParams);
                    if ( urlParams[HisSearchNonCriteriaUrlParam.NAVIGATION_POSITION] ) {
                        queryParams.navigationPosition = urlParams[HisSearchNonCriteriaUrlParam.NAVIGATION_POSITION];
                    }

                    if ( this.searchConfiguration.pageTitle ) { queryParams.pageTitle = this.searchConfiguration.pageTitle; }
                    if ( this.searchConfiguration.tableTitle ) { queryParams.tableTitle = this.searchConfiguration.tableTitle; }
                    if ( this.showAllSearchCriteria ) { queryParams.extended = true; }
                    if ( Array.isArray(result) ) { sessionStorage.setItem(HisSessionStorageKeys.CS_SYS_LAST_RESULT_CONFIG_FILES, result.join(',') ); }

                    // console.log('his-search-form#submitSearch() #search vor encode', search);
                    let searchItems: HisSearchItem[] = HisSearchParser.encode( search );
                    searchItems.push( new HisSearchItem( HisSearchConstants.SEARCH_CONFIG_FILE, this.searchConfiguration.configFile ) );
                    console.log('his-search-form#submitSearch() #searchItems', searchItems);
                    for ( let item of searchItems ) {
                        queryParams[item.key] = item.value;
                    }

                    // register the current search url path to use on header search buttons
                    queryParams.search = window.location.pathname;

                    // it checks if you are logged in.
                    // if you are not logged in, no list or table is shown in the route
                    if ( this.auth.hasAny( 'RIGHT_IS_LOGGED_IN' ) ) {
                        this.userProfileService.getValueforKey( this.searchResultModeUserProfileKey ).subscribe(
                            ( mode: string ) => {
                                queryParams.mode = mode;
                                if ( route === '../searchresult' ) {
                                    this.navigateToSearchResultByRelativeRoute( queryParams, route );
                                }
                            }
                        );
                    } else {
                        if ( route === '../searchresult' ) {
                            this.navigateToSearchResultByRelativeRoute( queryParams, route );
                        }
                    }
                },
                ( error: HttpErrorResponse ) => {
                    this.handleValidationErrors(error);
                }
            );
        }
    }

    private handleValidationErrors(error: HttpErrorResponse): Observable<any> {
        if (error.status !== this.ERROR_STATUS) {
            return throwError(() => new Error());
        }

        const { description, messages } = error.error;
        if (
            description !== this.EXCEPTION_DESCRIPTION ||
            !Array.isArray(messages)
        ) {
            return throwError(() => new Error());
        }

        const errorMessages = messages
            .map((m) => {
                const { field, message } = m;
                return { field, message };
            })
            .filter((m) => m.field && m.message) // skip if field or errorMessage is not present
            .map((m) => {
                const translatedLabel = this.translateLabelForField(m.field);
                if (!translatedLabel) {
                    return null; // skip if label translation is not available
                }
                return `${translatedLabel}: ${m.message}`;
            })
            .filter((m) => m); // filter out null values;

        if (errorMessages.length > 0) {
            this.notificationService.clear();
            this.notificationService.error(
                errorMessages,
                this.uiService.translate(this.ERROR_MESSAGE_TITLE),
            );
        }

        return of(null);
    }

    private translateLabelForField(field: string): string | null {
        const searchCriteria = this.searchConfiguration.searchCriteria;
        for (const group of searchCriteria.group) {
            for (const criteriaGroup of group.criteria) {
                if (criteriaGroup.path === field) {
                    return this.uiService.translate(criteriaGroup.label);
                }
            }
        }

        return null;
    }

    navigateToSearchResultByRelativeRoute( queryParams: Params, route: string ) {
        console.log( 'Current route:', this.route.url );
        console.log( 'Navigating to \'' + route + '\' with queryParams', queryParams );

        this.router.navigate( [route], {
            relativeTo: this.route,
            queryParams
        } );
    }

    getFormGroup() {
        return this.form;
    }

    isFormValid() {
        return this.form.valid;
    }

    getFormData() {
        if ( this.form ) {
            return this.form.value;
        }
    }

    // Eingaben zuruecksetzen
    resetInputs() {
        // log model (aka this.form.value) before reset
        console.log( 'Model before reset inputs =', this.getFormData() );

        // reset form inputs: triggers value changes that are noticed in init()
        if ( this.form ) {
            this.form.reset();
            this.searchService.setResetInputs( true );

            sessionStorage.removeItem( HisSessionStorageKeys.CS_SYS_LAST_SEARCH_CRITERIA_SET );
            this.resetWidgetsToDisplayFromSearchSet();

            // Meldungen zurücksetzen
            this.notificationService.clear();
        }

        //Remove all criteria group values and keep only the default values
        for ( const key of Object.keys( this.allParams ) ) {
            if ( key.startsWith( HisSearchConstants.GROUPS ) ) {
                delete this.allParams[key];
            }
        }

        // Explicitly clear all widget values before recreating the form.
        this.clearAllWidgetValues();

        this.initializeFormWithPresetValues( this.allParams );
    }

    private initializeFormWithPresetValues( params: Record<string, string> ) {
        if ( params ) {
            this.doPresetInputFields( params );
        }

        // create form group using the 'widget.value's as data model
        let group: any = {};
        for ( let widgetGroup of this.widgetGroups ) {
            for ( let widget of widgetGroup.widgets ) {

                group[widget.id] = new UntypedFormControl( widget.value );
                console.log( 'Assigned value', widget.value, 'to form control for widget', widget.id );


            }
        }
        this.form = new UntypedFormGroup( group );
        // Nach dem Laden von `widgetGroups` die Spalten berechnen
        this.calculateColumnGroups();
    }

    enterOnForm( event: any ) {
        const tagName = event.target.tagName.toLowerCase();
        if ( !this.showSearchButton && tagName === 'input' ) {
            event.preventDefault();
        }
    }

    private isChangeSearch(): boolean {
        return sessionStorage.getItem( HisSessionStorageKeys.CS_SYS_LAST_SEARCH_BUTTON ) === 'CHANGE_SEARCH';
    }

    private isNewSearch(): boolean {
        return sessionStorage.getItem( HisSessionStorageKeys.CS_SYS_LAST_SEARCH_BUTTON ) === 'NEW_SEARCH';
    }

    private storeSearchSetAtSessionStorage( searchCriteriaSet: HisSearchCriteriaSet ) {
        sessionStorage.setItem(
            HisSessionStorageKeys.CS_SYS_LAST_SEARCH_CRITERIA_SET, JSON.stringify( searchCriteriaSet ) );
    }

    private storeCompinentIdAtSessionStorage( componentId: string ) {
        sessionStorage.setItem(
            HisSessionStorageKeys.CS_SYS_SEARCH_CRITERIA_SET_COMPONENT_ID,
            componentId );
    }

    private getStandardSet( searchSets: HisSearchCriteriaSet[] ): HisSearchCriteriaSet {
        for ( let searchSet of searchSets ) {
            if ( searchSet.standard ) {
                return searchSet;
            }
        }
        return null;
    }

    private registerCurrentUrlForBackToSearchButton() {
        // register the current url for the "back to search" button
        console.log(
            'his-search-form.component#registerUrlForBackToSearchButton() #register url',
            'for the "back to search" button with standard set',
            '#window.location.href', window.location.href );
        this.navigationService.setSearchUrl( window.location.href );
        this.navigationService.setSearchResultUrl( null ); // forget last search result
    }

    /**
     * Berechnung der Aufteilung von Kriteriengruppen analog zu de.his.appclient.jsf.genericSearch.GenericSearchControllerBase.init()
     */
    private calculateColumnGroups(): void {
        if ( !this.widgetGroups || this.widgetGroups.length === 0 ) {
            console.log( 'widgetGroups ist leer oder nicht initialisiert.' );
            return;
        }

        const fieldsetBorderHeight = 1.7; // Analog zu JSF
        let rowCount = 0;

        // Berechnung der Gesamtanzahl an "Zeilen"
        this.widgetGroups.forEach( ( group ) => {
            rowCount += group.widgets.length + fieldsetBorderHeight;
        } );

        const halfRowCount = rowCount / 2;
        let count = 0;
        let i = 0;
        // Dynamische Aufteilung in linke und rechte Spalten
        for ( const group of this.widgetGroups ) {
            const size = group.widgets.length + fieldsetBorderHeight;
            if ( count + size > halfRowCount ) {
                const deltaLeft = halfRowCount - ( count + size );
                const deltaRight = halfRowCount - ( rowCount - count );
                if ( Math.abs( deltaLeft ) * 0.8 < Math.abs( deltaRight ) ) {
                    i++; // dann doch noch in die linke Spalten aufnehmen
                }
                this.searchMaskLeftGroupsAmount = i;
                break; // Beenden der Schleife, sobald searchMaskLeftGroupsAmount gesetzt wurde
            }
            count += size;
            i++;
        }
    }

    public scrollToResult() {
        this.formSearchButtonsComponentRef.nativeElement?.scrollIntoView( { behavior: 'smooth', block: 'start' } );
    }

}
